import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePocketBase } from '../contexts/PocketBaseContext';

const { FiArrowLeft, FiDownload, FiHeadphones, FiEye, FiBook, FiUser, FiCalendar, FiFileText, FiGlobe, FiTag } = FiIcons;

const BookDetailPage = () => {
  const { id } = useParams();
  const { apiCall } = usePocketBase();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBook();
  }, [id]);

  const fetchBook = async () => {
    try {
      setLoading(true);
      const record = await apiCall('GET', `/api/collections/books/records/${id}?expand=tags`);
      setBook(record);
    } catch (error) {
      console.error('Error fetching book:', error);
      setError('Book not found');
    } finally {
      setLoading(false);
    }
  };

  const getCoverUrl = () => {
    if (!book) return null;
    if (book.cover_file) {
      return `https://leaf-surrounded-my-book-hub.pockethost.io/api/files/${book.collectionId}/${book.id}/${book.cover_file}`;
    }
    if (book.cover_source_url) {
      return book.cover_source_url;
    }
    return null;
  };

  const getPdfUrl = () => {
    if (!book?.transcript_file) return null;
    return `https://leaf-surrounded-my-book-hub.pockethost.io/api/files/${book.collectionId}/${book.id}/${book.transcript_file}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to render rich text content safely
  const renderRichText = (content) => {
    if (!content) return null;
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // If it's plain text, return as-is
    if (tempDiv.textContent === content && !content.includes('<')) {
      return content.split('\n').map((paragraph, index) => (
        <p key={index} className="mb-4">
          {paragraph}
        </p>
      ));
    }
    
    // For HTML content, render with dangerouslySetInnerHTML
    return (
      <div 
        className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiBook} className="text-6xl text-gray-400 mb-4 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-600 mb-2">{error || 'Book not found'}</h2>
          <Link to="/" className="text-indigo-600 hover:text-indigo-700">
            ← Back to library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link to="/" className="inline-flex items-center space-x-2 text-indigo-600 hover:text-indigo-700 transition-colors">
            <SafeIcon icon={FiArrowLeft} />
            <span>Back to Library</span>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Book Cover and Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1"
          >
            <div className="sticky top-24">
              {/* Cover */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
                <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden mb-6">
                  {getCoverUrl() ? (
                    <img
                      src={getCoverUrl()}
                      alt={book.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full bg-gradient-to-br from-indigo-100 to-purple-100">
                      <SafeIcon icon={FiBook} className="text-6xl text-indigo-400" />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {book.transcript_file && (
                    <motion.a
                      href={getPdfUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center space-x-3 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:shadow-lg transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SafeIcon icon={FiDownload} className="text-xl" />
                      <span className="font-semibold">Download PDF</span>
                    </motion.a>
                  )}
                  {book.transcript_file && (
                    <motion.a
                      href={getPdfUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center space-x-3 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-2xl hover:shadow-lg transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SafeIcon icon={FiEye} className="text-xl" />
                      <span className="font-semibold">Read Online</span>
                    </motion.a>
                  )}
                  {book.audiobook_url && (
                    <motion.a
                      href={book.audiobook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center space-x-3 py-4 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-2xl hover:shadow-lg transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SafeIcon icon={FiHeadphones} className="text-xl" />
                      <span className="font-semibold">Listen to Audiobook</span>
                    </motion.a>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-white rounded-3xl shadow-xl p-6">
                <h3 className="font-bold text-lg mb-4 text-gray-900">Book Details</h3>
                <div className="space-y-3">
                  {book.pages && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiFileText} className="text-gray-400" />
                      <span className="text-gray-600">{book.pages} pages</span>
                    </div>
                  )}
                  {book.language && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiGlobe} className="text-gray-400" />
                      <span className="text-gray-600">{book.language}</span>
                    </div>
                  )}
                  {book.filesize_bytes && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiDownload} className="text-gray-400" />
                      <span className="text-gray-600">{formatFileSize(book.filesize_bytes)}</span>
                    </div>
                  )}
                  {book.words_count && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiBook} className="text-gray-400" />
                      <span className="text-gray-600">{book.words_count.toLocaleString()} words</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCalendar} className="text-gray-400" />
                    <span className="text-gray-600">Added {formatDate(book.created)}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Book Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 space-y-8"
          >
            {/* Title and Author */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {book.title}
              </h1>
              <div className="flex items-center space-x-2 text-xl text-gray-600 mb-6">
                <SafeIcon icon={FiUser} />
                <span>by {book.author}</span>
              </div>

              {/* Tags */}
              {book.expand?.tags && book.expand.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {book.expand.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 rounded-full text-sm font-medium"
                    >
                      <SafeIcon icon={FiTag} className="mr-2 text-xs" />
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Summary with Rich Text Support */}
            {book.summary && (
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Summary</h2>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h4]:text-base [&_p]:mb-4 [&_ul]:list-disc [&_ol]:list-decimal [&_li]:ml-6 [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_pre]:bg-gray-100 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_a]:text-indigo-600 [&_a]:hover:text-indigo-700 [&_strong]:font-semibold [&_em]:italic">
                  {renderRichText(book.summary)}
                </div>
              </div>
            )}

            {/* Transcript Preview */}
            {book.transcript_preview && (
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Preview</h2>
                <div className="bg-gray-50 rounded-2xl p-6 border-l-4 border-indigo-500">
                  <p className="text-gray-700 leading-relaxed font-mono text-sm">
                    {book.transcript_preview}
                  </p>
                  {book.transcript_preview.length >= 2000 && (
                    <p className="text-gray-500 text-sm mt-4 italic">
                      ... preview continues in full document
                    </p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookDetailPage;